import React, { FC, useState } from "react";
import classNames from "classnames";
import { useTranslation } from "react-i18next";
import { useBlogCategories, useBlogPosts } from "@app/hooks";
import { HeroArticlePreview } from "./components/hero-article-preview/HeroArticlePreview";
import { CategoriesSelect } from "./components/categories-select/CategoriesSelect";
import { ArticlesList } from "./components/articles-list/ArticlesList";
import { START_PAGE } from "./constants";

type Props = {
  category?: string;
};

export const Blog: FC<Props> = ({ category }) => {
  const [currentPage, setCurrentPage] = useState(START_PAGE);
  const { t } = useTranslation("p_blog");
  const categories = useBlogCategories();
  const posts = useBlogPosts();
  const [selectedCategories, setSelectedCategories] = useState(
    categories
      ? category
        ? categories.filter(
            (predefinedCategory) => predefinedCategory.uuid === category,
          )
        : categories
      : [],
  );
  const [selectedLabels, setSelectedLabels] = useState(
    category ? [category] : [],
  );
  const latestPost = posts[0];

  const otherPostsByCategories = posts
    .slice(1)
    .filter((post) =>
      selectedCategories.some(
        (selectedCategory) => selectedCategory.uuid === post.categoryId,
      ),
    );

  const onSelectedCategoryChange = (categoryId: string) => {
    const isAlreadySelected = selectedCategories.some(
      (selectedCategory) => selectedCategory.uuid === categoryId,
    );
    const isLabelAlreadySelectd = selectedLabels.some(
      (label) => label === categoryId,
    );

    setSelectedLabels(
      isLabelAlreadySelectd
        ? selectedLabels.filter((label) => label !== categoryId)
        : [...selectedLabels, categoryId],
    );

    if (
      selectedCategories.length === categories.length &&
      !selectedLabels.length
    ) {
      setSelectedCategories(
        selectedCategories.filter(
          (selectedCategory) => selectedCategory.uuid === categoryId,
        ),
      );

      return;
    }

    setSelectedCategories(
      isAlreadySelected
        ? selectedCategories.length === 1
          ? categories
          : selectedCategories.filter(
              (selectedCategory) => selectedCategory.uuid !== categoryId,
            )
        : [
            ...selectedCategories,
            categories.filter(
              (predefinedCategory) => predefinedCategory.uuid === categoryId,
            )[0],
          ],
    );
  };

  return (
    <div className={classNames("px-5 pt-24", "lg:px-16", "xl:px-24")}>
      <div className={classNames("max-w-container", "xxl:mx-auto")}>
        <div
          className={classNames("py-15 text-center", "lg:py-25", "xl:py-40")}
        >
          <h1
            className={classNames(
              "text-large text-primary-content",
              "lg:text-huge",
            )}
          >
            {t("hero_title")}
          </h1>
        </div>

        <div className="flex justify-center">
          <HeroArticlePreview {...latestPost} />
        </div>

        <div
          className={classNames("py-15", "lg:py-25", "xl:py-40")}
          id="articlesList"
        >
          <CategoriesSelect
            {...{
              categories,
              selectedLabels,
              onChange: onSelectedCategoryChange,
            }}
          />

          {otherPostsByCategories.length && (
            <ArticlesList
              list={otherPostsByCategories}
              currentPage={currentPage}
              setCurrentPage={() => setCurrentPage(currentPage + 1)}
            />
          )}
        </div>
      </div>
    </div>
  );
};
